import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  ClassListParams,
  CreateClassInput,
  JoinCodeAction,
  PromoteClassInput,
  UpdateClassInput,
} from './types';

/** Query-key factory — every classes key derives from here. */
export const classKeys = {
  all: ['classes'] as const,
  lists: () => [...classKeys.all, 'list'] as const,
  list: (params: ClassListParams) => [...classKeys.lists(), params] as const,
  details: () => [...classKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...classKeys.details(), publicId] as const,
  joinCode: (publicId: string) => [...classKeys.all, 'join-code', publicId] as const,
};

export function useClasses(params: ClassListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: classKeys.list(params),
    queryFn: () => api.getClasses(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useClass(publicId: string) {
  return useQuery({
    queryKey: classKeys.detail(publicId),
    queryFn: () => api.getClass(publicId),
    enabled: Boolean(publicId),
  });
}

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClassInput) => api.createClass(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: classKeys.lists() }),
  });
}

export function useUpdateClass(classPublicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClassInput) => api.updateClass(classPublicId, input),
    // The PATCH responds with the write read-back (teacher ids only) — refetch
    // the detail/list payloads for the expanded teacher objects.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: classKeys.detail(classPublicId) });
      void qc.invalidateQueries({ queryKey: classKeys.lists() });
    },
  });
}

export function useArchiveClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.archiveClass(publicId),
    onSuccess: (_data, publicId) => {
      void qc.invalidateQueries({ queryKey: classKeys.lists() });
      void qc.invalidateQueries({ queryKey: classKeys.detail(publicId) });
    },
  });
}

export function usePromoteClass(classPublicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PromoteClassInput) => api.promoteClass(classPublicId, input),
    onSuccess: promoted => {
      qc.setQueryData(classKeys.detail(promoted.public_id), promoted);
      void qc.invalidateQueries({ queryKey: classKeys.lists() });
    },
  });
}

export function useUnarchiveClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.unarchiveClass(publicId),
    onSuccess: detail => {
      qc.setQueryData(classKeys.detail(detail.public_id), detail);
      void qc.invalidateQueries({ queryKey: classKeys.lists() });
    },
  });
}

export function useEnrollPupil(classPublicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberPublicId: string) => api.enrollPupil(classPublicId, memberPublicId),
    onSuccess: detail => {
      // The action responds with the refreshed detail — use it directly.
      qc.setQueryData(classKeys.detail(classPublicId), detail);
      void qc.invalidateQueries({ queryKey: classKeys.lists() }); // pupil counts
    },
  });
}

export function useUnenrollPupil(classPublicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberPublicId: string) => api.unenrollPupil(classPublicId, memberPublicId),
    onSuccess: detail => {
      qc.setQueryData(classKeys.detail(classPublicId), detail);
      void qc.invalidateQueries({ queryKey: classKeys.lists() });
    },
  });
}

/**
 * Read the class's join code. The API has no GET for it, but the `create`
 * action is an idempotent get-or-create (ADR-008), so it doubles as the read.
 * Gated behind `enabled` — the card fetches only after an explicit reveal, so
 * merely opening the page never mints a code.
 */
export function useJoinCode(classPublicId: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: classKeys.joinCode(classPublicId),
    queryFn: () => api.joinCodeAction(classPublicId, 'create'),
    enabled: options.enabled,
    staleTime: Infinity, // only our own actions change it — they update the cache
    gcTime: Infinity,
  });
}

export function useJoinCodeAction(classPublicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: JoinCodeAction) => api.joinCodeAction(classPublicId, action),
    onSuccess: joinCode => qc.setQueryData(classKeys.joinCode(classPublicId), joinCode),
  });
}

export function useJoinClass() {
  return useMutation({
    mutationFn: (code: string) => api.joinClassByCode(code),
    // No invalidation: the caller refreshes the whole session (a new
    // membership/enrollment changes the bootstrap snapshot).
  });
}
